// Croqui: `components/Icon.tsx`. Os 38 glifos do canvas, 24x24, traço 1,5.
//
// Coexiste com `lucide-react`: a nav (`app/nav-seller.ts`, `app/nav-admin.ts`) é registro
// compartilhado e continua em lucide (`spec/17` §5.3). Aqui ficam os ícones que os
// componentes do kit recebem por nome (`Btn`, `Stat`, `Empty`, `Notice`, `IconTile`).

import type { ReactElement } from 'react';
import { cn } from '../../lib/cn.ts';

export type IconName =
  | 'grid'
  | 'swap'
  | 'wallet'
  | 'arrow'
  | 'key'
  | 'doc'
  | 'code'
  | 'webhook'
  | 'bolt'
  | 'book'
  | 'user'
  | 'shield'
  | 'percent'
  | 'lock'
  | 'bell'
  | 'users'
  | 'check'
  | 'ledger'
  | 'scale'
  | 'plug'
  | 'inbox'
  | 'cpu'
  | 'chart'
  | 'alert'
  | 'list'
  | 'gear'
  | 'search'
  | 'store'
  | 'sun'
  | 'moon'
  | 'logout'
  | 'dots'
  | 'ingot'
  | 'chevronDown'
  | 'chevronLeft'
  | 'chevronRight'
  | 'close'
  | 'copy';

const P = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;
const P2 = { ...P, strokeWidth: 2 } as const;

const PATHS: Record<IconName, ReactElement> = {
  grid: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" {...P} />
      <rect x="14" y="3" width="7" height="7" rx="1.5" {...P} />
      <rect x="3" y="14" width="7" height="7" rx="1.5" {...P} />
      <rect x="14" y="14" width="7" height="7" rx="1.5" {...P} />
    </>
  ),
  swap: (
    <>
      <path d="M4 8h13l-3-3" {...P} />
      <path d="M20 16H7l3 3" {...P} />
    </>
  ),
  wallet: (
    <>
      <rect x="3" y="6" width="18" height="13" rx="3" {...P} />
      <path d="M3 10h18M16 14h2" {...P} />
    </>
  ),
  arrow: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="4" {...P} />
      <path d="M8 16l8-8M10 8h6v6" {...P} />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="14" r="4" {...P} />
      <path d="M11 11l9-9M16 6l2 2M13 9l2 2" {...P} />
    </>
  ),
  doc: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2.5" {...P} />
      <path d="M8 8h8M8 12h8M8 16h5" {...P} />
    </>
  ),
  code: <path d="M8 7l-5 5 5 5M16 7l5 5-5 5M14 4l-4 16" {...P} />,
  webhook: (
    <>
      <circle cx="6" cy="17" r="3" {...P} />
      <circle cx="18" cy="17" r="3" {...P} />
      <circle cx="12" cy="7" r="3" {...P} />
      <path d="M12 10v4M9.5 15l-1.5-1M14.5 15l1.5-1" {...P} />
    </>
  ),
  bolt: <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" {...P} />,
  book: (
    <>
      <path d="M4 5a2 2 0 012-2h13v16H6a2 2 0 00-2 2V5z" {...P} />
      <path d="M4 19a2 2 0 012-2h13" {...P} />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" {...P} />
      <path d="M4 21a8 8 0 0116 0" {...P} />
    </>
  ),
  shield: (
    <>
      <path d="M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-3z" {...P} />
      <path d="M9 12l2 2 4-4" {...P} />
    </>
  ),
  percent: (
    <>
      <path d="M19 5L5 19" {...P} />
      <circle cx="7" cy="7" r="2.5" {...P} />
      <circle cx="17" cy="17" r="2.5" {...P} />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="10" rx="2.5" {...P} />
      <path d="M8 11V7a4 4 0 018 0v4" {...P} />
    </>
  ),
  bell: (
    <>
      <path d="M6 16V11a6 6 0 0112 0v5l2 2H4l2-2z" {...P} />
      <path d="M10 20a2 2 0 004 0" {...P} />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.2" {...P} />
      <path d="M3.5 19a5.5 5.5 0 0111 0" {...P} />
      <path d="M16 5.5a3 3 0 010 5.5M17.5 14.5a5 5 0 013 4.5" {...P} />
    </>
  ),
  check: (
    <>
      <circle cx="12" cy="12" r="9" {...P} />
      <path d="M8 12l3 3 5-6" {...P} />
    </>
  ),
  ledger: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2.5" {...P} />
      <path d="M8 3v18M12 8h4M12 12h4" {...P} />
    </>
  ),
  scale: <path d="M12 3v18M4 7h16M6 7l-3 7a3 3 0 006 0L6 7zM18 7l-3 7a3 3 0 006 0l-3-7z" {...P} />,
  plug: <path d="M9 3v5M15 3v5M6 8h12v3a6 6 0 01-12 0V8zM12 17v4" {...P} />,
  inbox: (
    <>
      <path d="M3 13l2-8h14l2 8v6H3v-6z" {...P} />
      <path d="M3 13h5l2 3h4l2-3h5" {...P} />
    </>
  ),
  cpu: (
    <>
      <rect x="6" y="6" width="12" height="12" rx="2" {...P} />
      <rect x="10" y="10" width="4" height="4" {...P} />
      <path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4" {...P} />
    </>
  ),
  chart: (
    <>
      <path d="M4 20V4M4 20h16" {...P} />
      <path d="M8 15l4-5 3 3 5-6" {...P} />
    </>
  ),
  alert: (
    <>
      <path d="M12 3l10 18H2L12 3z" {...P} />
      <path d="M12 10v4M12 17v.5" {...P} />
    </>
  ),
  list: <path d="M9 6h11M9 12h11M9 18h11M4 6h.5M4 12h.5M4 18h.5" {...P} />,
  gear: (
    <>
      <circle cx="12" cy="12" r="3" {...P} />
      <path
        d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1L7 17M17 7l2.1-2.1"
        {...P}
      />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" {...P} />
      <path d="M16 16l4 4" {...P} />
    </>
  ),
  store: (
    <>
      <path d="M4 10V20h16V10" {...P} />
      <path d="M3 6l1.5-3h15L21 6v2a3 3 0 01-6 0 3 3 0 01-6 0 3 3 0 01-6 0V6z" {...P} />
      <path d="M10 20v-5h4v5" {...P} />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" {...P} />
      <path
        d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"
        {...P}
      />
    </>
  ),
  moon: <path d="M20 14.5A8 8 0 019.5 4a8 8 0 1010.5 10.5z" {...P} />,
  logout: (
    <>
      <path d="M10 4H6a2 2 0 00-2 2v12a2 2 0 002 2h4" {...P} />
      <path d="M14 8l4 4-4 4M18 12H9" {...P} />
    </>
  ),
  dots: (
    <>
      <circle cx="6" cy="12" r="1.4" fill="currentColor" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
      <circle cx="18" cy="12" r="1.4" fill="currentColor" />
    </>
  ),
  /* Barras de ouro: duas embaixo, uma em cima; preenchido, com brilho. */
  ingot: (
    <>
      <path d="M8.5 4.5h7l2 5h-11z" fill="currentColor" />
      <path d="M2.5 13h8.5l2 6.5H1z" fill="currentColor" />
      <path d="M13 13h8.5l2 6.5H11z" fill="currentColor" />
      <path
        d="M10 6.2h4M4 14.8h4.2M14.6 14.8h4.2"
        stroke="rgba(255,255,255,.55)"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </>
  ),
  chevronDown: <path d="M6 9l6 6 6-6" {...P2} />,
  chevronLeft: <path d="M15 6l-6 6 6 6" {...P2} />,
  chevronRight: <path d="M9 6l6 6-6 6" {...P2} />,
  close: <path d="M6 6l12 12M18 6L6 18" {...P2} />,
  copy: (
    <>
      <rect x="9" y="9" width="11" height="11" rx="2" {...P} />
      <path d="M5 15V6a2 2 0 012-2h9" {...P} />
    </>
  ),
};

export const ICON_NAMES = Object.keys(PATHS) as IconName[];

/**
 * Ícone do kit. Decorativo por default (`aria-hidden`): quem carrega o significado é o
 * texto ao lado. Sozinho num botão, passe `label`, e o botão ganha `aria-label`
 * (`spec/17` §7 item 12).
 */
export function Icon({
  name,
  size = 18,
  label,
  className,
}: {
  name: IconName;
  size?: number;
  label?: string;
  className?: string;
}) {
  // Os dois ramos são escritos por extenso (e não um spread de atributos) porque o lint de
  // a11y do Biome (`noSvgWithoutTitle`) lê o JSX estaticamente: um `aria-hidden` vindo de
  // spread ele não enxerga.
  if (label === undefined) {
    return (
      <svg
        aria-hidden="true"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        className={cn('shrink-0', className)}
      >
        {PATHS[name]}
      </svg>
    );
  }
  return (
    <svg
      role="img"
      aria-label={label}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={cn('shrink-0', className)}
    >
      <title>{label}</title>
      {PATHS[name]}
    </svg>
  );
}
